import { globalStores } from '@/editor/stores';
import { findEntityInfo } from '@/editor/entities/utils';
import { ETransformType, IIntersectedModelEntityInfo } from '@/editor/types/entity';

import { OrthographicCamera, Object3D, Raycaster, Vector2, Vector3 } from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';

// 线条拾取的容差（屏幕像素）。线条只有 1px，取较小值使得只有鼠标移到线上才命中，而非附近
const LINE_PICK_PIXEL_THRESHOLD = 4;

class RayIntersect {
  private _raycaster = new Raycaster();

  rayEntities(mousePosition: Vector2): IIntersectedModelEntityInfo[] {
    const { camera } = globalStores.editorStateStore.sceneInfo.scene2d;
    // 先检测门窗图层
    this._raycaster.setFromCamera(mousePosition, camera);
    // 线条拾取容差按屏幕像素换算到世界坐标，避免缩放后“附近即选中”
    this._raycaster.params.Line.threshold = this._getLinePickThreshold(camera);

    const intersects = this._raycaster.intersectObject(
      globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup,
      true
    );

    if (intersects.length > 0) {
      return (
        intersects
          .map((item) => {
            const entityInfo = findEntityInfo(item.object);
            return entityInfo
              ? {
                  ...entityInfo,
                  intersectPoint: item.point.clone()
                }
              : null;
          })
          .filter(Boolean) as IIntersectedModelEntityInfo[]
      ).sort((a, b) => {
        const priorityA = a.priority || 0;
        const priorityB = b.priority || 0;
        return priorityB - priorityA; // 降序排列
      });
    }

    return [];
  }

  /**
   * 将屏幕像素容差换算为正交相机下的世界坐标容差。
   * 几何体坐标已除以 pixelRatio2d，与相机视锥同处一个坐标系，故无需再做单位换算。
   */
  private _getLinePickThreshold(camera: OrthographicCamera): number {
    const canvasWidth = globalStores.editorStateStore.sceneInfo.renderer.domElement.clientWidth;
    if (!canvasWidth) return LINE_PICK_PIXEL_THRESHOLD;

    const worldPerPixel = (camera.right - camera.left) / camera.zoom / canvasWidth;
    return worldPerPixel * LINE_PICK_PIXEL_THRESHOLD;
  }

  rayTransformType(point: IPoint2d, object: Object3D): ETransformType {
    this._raycaster.set(new Vector3(point.x / pixelRatio2d, point.y / pixelRatio2d, 100), new Vector3(0, 0, -1));

    const intersects = this._raycaster.intersectObject(object, true);
    if (!intersects.length) return ETransformType.Empty;

    const intersect = intersects[0];

    switch (intersect.object.name) {
      case 'rotation':
        return ETransformType.Rotation;
      case 'left':
        return ETransformType.ScaleLeft;
      case 'right':
        return ETransformType.ScaleRight;
      case 'top':
        return ETransformType.ScaleTop;
      case 'bottom':
        return ETransformType.ScaleBottom;
      case 'leftTop':
        return ETransformType.ScaleLeftTop;
      case 'leftBottom':
        return ETransformType.ScaleLeftBottom;
      case 'rightTop':
        return ETransformType.ScaleRightTop;
      case 'rightBottom':
        return ETransformType.ScaleRightBottom;
      default:
        return ETransformType.Empty;
    }
  }
}

export const rayIntersect = new RayIntersect();
